import { LngLatLike, LngLat } from 'maplibre-gl'
import config from '@src/config'
import axios from 'axios'
import { AxiosRequestConfig } from 'axios'
import { TravelMode, GeometryType, AvoidType, OverviewType, ApproachType } from '@src/common/interface'
import { NBRequestStatus, StepIntersections, StepManeuver } from './common'

export interface NavigationBearingInfo {
  degree: number
  range: number
}

/**
 * A navigation query to be sent to the NavigationSerivce
 */
export type NavigationRequest = {
  /**
   * Origin is the starting point of your route. Ensure that origin is a valid land location.
   *
   * [This parameter is mandatory IF geometry parameter is not given]
   */
  origin?: LngLatLike
  /**
   * Destination is the ending coordinates of your route. Ensure that destination is a valid land location.
   *
   * [This parameter is mandatory IF geometry parameter is not given]
   *
   */
  destination?: LngLatLike
  /**
   * Waypoints are coordinates along the route between Origin and Destination. This is a pipe separated list of coordinate pairs.
   *
   * Default maximum limit is 200
   *
   */
  waypoints?: LngLatLike[]
  /** A parameter which which sets the transportation mode for the route. PS: Only the 4w/car profile is enabled by default. The other customised routing profiles are only supported in premium plan. */
  mode?: TravelMode | string
  /**
   * language of the text instruction
   */
  lang?: string
  /**
   * If this parameter is provided, the only other parameters which will be considered in the request are `geometry_type`, `lang` and `key`. The rest of the parameters will be ignored.
   */
  geometry?: string
  /** This parameter sets the departure time in Unix epoch time seconds format. Unit: seconds. PS: This parameter is only supported in premium plan. */
  departureTime?: number
  /** Unique session ID. If set, the response will reuse cached trip characteristics. PS: This parameter is only supported in premium plan. */
  session?: string
  /** Sets the output format of the route geometry in the response. Default: `polyline6`. */
  geometryType?: GeometryType
  /**
   * If enabled, the API will return alternative routes.
   *
   * You can set the number of routes in the `altCount` property. Note: `altcount` will default to `3` if this is enabled.
   */
  alternatives?: boolean
  /** Sets the number of alternative routes to return. Default: `3`. */
  altCount?: number
  /** Setting this will ensure the route avoids ferry, tolls or highways. */
  avoid?: AvoidType[]
  /** Output verbosity of overview (whole trip) geometry. Default: `full`. */
  overview?: OverviewType
  /**
   * Limits the search to segments with given bearing in degrees towards true north in clockwise direction.
   *
   * Note: Number of bearings should be equal to the number of coordinates.
   */
  bearings?: NavigationBearingInfo[]
  /** A list indicating the side of the road from which to approach waypoints in a requested route. If provided, the number of approaches must be one more than the number of waypoints. */
  approaches?: ApproachType[]
  /**
   * api key for the request
   */
  apiKey?: string
}

interface NormalizedNavigationRequest extends NavigationRequest {
  /** The starting point of a route. */
  origin: LngLat
  /** The ending point of a route. */
  destination: LngLat
  /** Coordinates along the route between the Origin and the Destination. */
  waypoints?: LngLat[]
}

/**
 * The navigation response retrieved from the navigation server.
 */
export type NavigationResult = {
  country_code?: string
  /** A string indicating the state of the response. This is a separate code than the HTTP status code. On normal valid responses, the value will be Ok. */
  status: NBRequestStatus | string
  /** error message if status is not 200. */
  error_msg?: string
  /** routes calculated. */
  routes?: Array<NavigationResponseRoute>
}

/**
 * @interface NavigationResponseRoute
 * The route information returned by the navigation service.
 */
export interface NavigationResponseRoute {
  /**
   * route driving distance.
   *
   * Unit: `meters`
   */
  distance: number
  distance_full?: number
  /**
   * route driving duration.
   *
   * Unit: `seconds`
   */
  duration: number
  geojson?: {
    coordinates: number[][]
  }
  /** encoded geometry value for step in `polyline` or `polyline6`. */
  geometry?: string
  /**
   * start location of route
   */
  start_location?: {
    latitude: number
    longitude: number
  }
  /**
   * end location of route
   */
  end_location?: {
    latitude: number
    longitude: number
  }
  /**
   * legs of route.
   *
   * Note: `waypoints` split `route` into `legs`
   */
  legs: Array<NavigationResponseLeg>
  /**
   * special geospatial objects crossed along the trip.
   */
  special_objects?: any
}

/**
 * @interface NavigationResponseLeg
 * The leg information in the route.
 */
export interface NavigationResponseLeg {
  /**
   * leg driving distance.
   *
   * Unit: `meters`
   */
  distance: {
    value: number
  }
  /**
   * leg driving duration.
   *
   * Unit: `seconds`
   */
  duration: {
    value: number
  }
  start_location?: {
    latitude: number
    longitude: number
  }
  end_location?: {
    latitude: number
    longitude: number
  }
  steps: Array<NavigationResponseLegStep>
}

/**
 * @interface NavigationResponseLegStep
 * The step information in the leg.
 */
export interface NavigationResponseLegStep {
  name?: string
  reference?: string
  geometry?: string
  /**
   * leg driving distance.
   *
   * Unit: `meters`
   */
  distance: {
    value: number
  }
  /**
   * leg driving duration.
   *
   * Unit: `seconds`
   */
  duration: {
    value: number
  }
  geojson?: {
    coordinates: Array<Array<number>>
  }
  intersections?: Array<StepIntersections>
  maneuver?: StepManeuver
  start_location: {
    latitude: number
    longitude: number
  }
  end_location: {
    latitude: number
    longitude: number
  }
}

function checkAndnormalizeOpt(opt: NavigationRequest): NormalizedNavigationRequest {
  if (!opt.geometry) {
    if (!opt.origin) {
      throw new Error('origin is required in NavigationRequest if geometry is not provided')
    }
    if (!opt.destination) {
      throw new Error('destination is required in NavigationRequest if geometry is not provided')
    }
  }

  const nOpt: NormalizedNavigationRequest = Object.assign({}, opt, {
    origin: opt.origin ? LngLat.convert(opt.origin) : opt.origin,
    destination: opt.destination ? LngLat.convert(opt.destination) : opt.destination,
    waypoints: opt.waypoints ? opt.waypoints.map((w) => LngLat.convert(w)) : opt.waypoints,
  }) as NormalizedNavigationRequest

  if (nOpt.approaches) {
    if (nOpt.waypoints) {
      if (nOpt.waypoints.length + 1 !== nOpt.approaches.length) {
        throw new Error('The number of approaches must be one more than the number of waypoints.')
      }
    } else {
      if (nOpt.approaches.length !== 1) {
        throw new Error('The number of approaches must be one more than the number of waypoints. ')
      }
    }
  }
  return nOpt
}

/**
 * A service for calculating navigation between locations.
 * Can be accessed by `nextbillion.maps.NavigationService` if you load SDK by CDN url.
 */
export class NavigationService {
  /**
   * Request navigation between an origin and a destination.
   * @param opt request options
   * @returns navigation result of the request
   */
  async route(opt: NavigationRequest): Promise<NavigationResult | null> {
    if (!config.API_KEY && !opt.apiKey) {
      throw new Error('In order to call the NextBillion.ai API, you must provide a valid API Key.')
    }

    if (!config.API_HOST) {
      throw new Error('An API host is required to make request to NextBillion.ai API.')
    }

    const nOpt = checkAndnormalizeOpt(opt)

    const params: any = {}

    if (nOpt.geometry) {
      params.geometry = nOpt.geometry
    }

    if (nOpt.origin) {
      params.origin = `${nOpt.origin.lat},${nOpt.origin.lng}`
    }

    if (nOpt.destination) {
      params.destination = `${nOpt.destination.lat},${nOpt.destination.lng}`
    }

    if (Array.isArray(nOpt.waypoints)) {
      const waypoints = nOpt.waypoints.map((point) => `${point.lat},${point.lng}`).join('|')
      params['waypoints'] = waypoints
    }

    if (nOpt.geometryType) {
      params.geometry_type = nOpt.geometryType
    }

    if (nOpt.mode) {
      params.mode = nOpt.mode
    }

    if (nOpt.lang) {
      params.lang = nOpt.lang
    }

    if (nOpt.alternatives) {
      params.alternatives = nOpt.alternatives
    }

    if (nOpt.altCount) {
      params.altcount = nOpt.altCount
    }

    if (nOpt.geometry) {
      params.geometry = nOpt.geometry
    }

    if (nOpt.avoid) {
      params.avoid = nOpt.avoid.join('|')
    }

    if (nOpt.overview) {
      params.overview = nOpt.overview
    }

    if (nOpt.bearings) {
      params.bearings = nOpt.bearings
    }

    if (nOpt.departureTime) {
      params.departure_time = nOpt.departureTime
    }

    if (nOpt.approaches) {
      params.approaches = nOpt.approaches.join(';')
    }

    const reqConfig: AxiosRequestConfig = {
      params,
    }
    if (config.API_KEY_HEADER_NAME) {
      reqConfig.headers = {
        [config.API_KEY_HEADER_NAME]: nOpt.apiKey || config.API_KEY,
      }
    } else {
      params.key = nOpt.apiKey || config.API_KEY
    }

    return await axios
      .get(`https://${config.API_HOST}/navigation/json`, reqConfig)
      .then((resp) => resp.data as NavigationResult)
      .catch((err) => {
        if (err.response && err.response.data) {
          return err.response.data as NavigationResult
        } else {
          return err
        }
      })
  }
}
